import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const phases = [
  { phase: "PHASE 1", label: "Malaysia", zh: "马来西亚", status: "LIVE", color: "#FF1C35", desc: "Validated production & distribution model. Malaysia's #1 local short drama platform.", icon: "🇲🇾" },
  { phase: "PHASE 2", label: "Indonesia", zh: "印度尼西亚", status: "EXPANDING", color: "#c8a96e", desc: "Cultural affinity · Largest SEA market · Priority expansion target.", icon: "🇮🇩" },
  { phase: "PHASE 3", label: "Southeast Asia", zh: "东南亚全域", status: "ROADMAP", color: "#82c996", desc: "600M+ population · Replicable localization model across all SEA markets.", icon: "🌏" },
];

const principles = [
  { en: "Build Local Teams", zh: "建立本地制作团队", icon: "👥" },
  { en: "Localized Content", zh: "内容本地化适配", icon: "🎬" },
  { en: "Replicate Formats", zh: "成功模式复制", icon: "♻️" },
  { en: "Data-Driven Growth", zh: "数据驱动扩张", icon: "📈" },
];

const Expansion = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} style={{ padding: "100px 40px", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={visible ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{ width: 36, height: 2, background: "#FF1C35" }} />
            <span style={{ color: "#FF1C35", fontSize: 12, fontWeight: 700, letterSpacing: 3 }}>EXPANSION ROADMAP · 扩张路线图</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16, marginBottom: 56 }}>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 900, lineHeight: 1.1, margin: 0 }}>
              From Malaysia to<br /><span style={{ color: "#FF1C35" }}>Southeast Asia</span>
            </h2>
            <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 14, margin: 0, maxWidth: 320, lineHeight: 1.7 }}>
              Building a regional content network connecting multiple SEA markets under one unified platform ecosystem.
            </p>
          </div>
        </motion.div>

        {/* Phase cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, marginBottom: 48 }} className="expansion-grid">
          {phases.map((p, i) => (
            <motion.div key={p.phase}
              initial={{ opacity: 0, y: 24 }}
              animate={visible ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.15, duration: 0.6 }}
              style={{
                padding: "32px 24px", borderRadius: 18,
                background: i === 0 ? "rgba(255,28,53,0.06)" : "rgba(255,255,255,0.02)",
                border: `1px solid ${i === 0 ? "rgba(255,28,53,0.25)" : "rgba(255,255,255,0.06)"}`,
                position: "relative", overflow: "hidden",
              }}
            >
              {/* Glow for active */}
              {i === 0 && <div style={{ position: "absolute", top: -40, right: -40, width: 120, height: 120, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,28,53,0.2) 0%, transparent 70%)" }} />}
              
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                <span style={{ fontSize: 36 }}>{p.icon}</span>
                <span style={{
                  padding: "5px 12px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                  background: `${p.color}18`, color: p.color, border: `1px solid ${p.color}30`,
                }}>{p.status}</span>
              </div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", fontWeight: 700, letterSpacing: 2, marginBottom: 4 }}>{p.phase}</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", marginBottom: 2 }}>{p.label}</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.3)", marginBottom: 14 }}>{p.zh}</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.7 }}>{p.desc}</div>
            </motion.div>
          ))}
        </div>

        {/* Expansion principles */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={visible ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.5, duration: 0.6 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }} className="principles-grid">
            {principles.map((p) => (
              <div key={p.en} style={{
                padding: "18px 16px", borderRadius: 12,
                background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)",
                textAlign: "center",
              }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>{p.icon}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{p.en}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginTop: 3 }}>{p.zh}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
      <style>{`
        @media (max-width: 768px) { .expansion-grid { grid-template-columns: 1fr !important; } .principles-grid { grid-template-columns: 1fr 1fr !important; } }
      `}</style>
    </section>
  );
};

export default Expansion;
